import { Fragment, forwardRef, useState } from "react";
import { TbLogout, TbUserCircle } from "react-icons/tb";
import { useUiLabels } from "../labels/LabelsContext";
import ConfirmationModal from "../ConfirmationModal";
import Clock from "./Clock";
import HeaderMenu from "./HeaderMenu";
import { Icon } from "../Icon";
import "./styles/index.scss";

// Icon button of the header bar (icon: a Tabler icon; label: its accessible name). Apps use it for their own
// actions (children: panels, badges).
export const HeaderAction = forwardRef(function HeaderAction({ className, icon, label, onClick, children }, ref) {
  return (
    <div className={className} role="button" aria-label={label} onClick={onClick} ref={ref}>
      <Icon icon={icon} size="lg" aria-hidden="true" />
      {children}
    </div>
  );
});

export { HeaderMenu };

// "Mario Rossi" -> "MR", "fixture_client" -> "FC", "mario" -> "M".
const getInitials = (name) =>
  String(name || "")
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

// Menu entries ({ key, label, element }) in alphabetical order of their label; the logout is added after them.
const sortedEntries = (entries, locale) =>
  [...entries].sort((a, b) => String(a.label).localeCompare(String(b.label), locale, { sensitivity: "base" }));

// Desktop header bar: logo, clock, env badge, [centre], then the user block.
// User block, legacy: user badge/welcome, profile, app actions (children), logout.
// User block with `account` ({ name, role, items, locale }): app actions (children, e.g. the bell) and, to their
// right, a dropdown opened by the avatar with name and role. It lists the profile (onProfile; null: no
// profile entry, for an account with no profile page) and the entries in account.items ({ key, label, element }), alphabetically by label, then the logout; badge and welcome are not shown.
// envLabel / welcome / center: null hides them. center: content centred between the left and user blocks
// (a string gets ellipsis; a node is rendered as is and may hold its own dropdown). formatDate: digital clock text.
const HeaderShell = ({
  isMobile,
  logo,
  formatDate,
  envLabel,
  center,
  account,
  badge,
  welcome,
  onProfile,
  onLogout,
  children,
}) => {
  const [getLabel] = useUiLabels();
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);

  if (isMobile) {
    return null;
  }

  return (
    <div className="header">
      <div className={`header_wrapper${center ? " has_center" : ""}`}>
        <div className="header_wrapper_left">
          <div className="header_wrapper_logo">
            <img className="logo" src={logo} alt="Logo" />
          </div>
          <div className="header_wrapper_clock">
            <Clock formatDate={formatDate} />
          </div>
          {envLabel && (
            <div className="header_wrapper_left_env_badge">{envLabel}</div>
          )}
        </div>
        {center && (
          <div className="header_wrapper_center">
            {typeof center === "string" ? <span className="header_wrapper_center_text">{center}</span> : center}
          </div>
        )}
        {account ? (
          <div className="header_wrapper_user">
            {children}
            <HeaderMenu
              className="header_account"
              trigger={
                <>
                  <span className="header_account_avatar" aria-hidden="true">
                    {getInitials(account.name)}
                  </span>
                  <span className="header_account_text">
                    <span className="header_account_name">{account.name}</span>
                    <span className="header_account_role">{account.role}</span>
                  </span>
                </>
              }
            >
              {sortedEntries(
                [
                  onProfile !== null && {
                    key: "profile",
                    label: getLabel("headerProfile"),
                    element: (
                      <button type="button" className="header_menu_item" onClick={onProfile}>
                        <Icon icon="fa-user" size="md" aria-hidden="true" />
                        {getLabel("headerProfile")}
                      </button>
                    ),
                  },
                  ...(account.items || []),
                ].filter(Boolean),
                account.locale
              ).map((entry) => (
                <Fragment key={entry.key}>{entry.element}</Fragment>
              ))}
              <button
                type="button"
                className="header_menu_item header_menu_item_logout"
                onClick={() => setIsLogoutOpen(true)}
              >
                <Icon icon="fa-right-from-bracket" size="md" aria-hidden="true" />
                {getLabel("headerLogout")}
              </button>
            </HeaderMenu>
          </div>
        ) : (
          <div className="header_wrapper_user">
            <div className="header_wrapper_user_badge">{badge}</div>
            {welcome != null && (
              <div className="header_wrapper_user_label">{welcome}</div>
            )}
            <HeaderAction className="header_wrapper_user" icon={TbUserCircle} onClick={onProfile} />
            {children}
            <HeaderAction
              className="header_wrapper_user_setting"
              icon={TbLogout}
              onClick={() => setIsLogoutOpen(true)}
            />
          </div>
        )}
      </div>
      <ConfirmationModal
        isOpen={isLogoutOpen}
        onConfirm={onLogout}
        onClose={() => setIsLogoutOpen(false)}
        description={getLabel("modalLogout")}
      ></ConfirmationModal>
    </div>
  );
};

export default HeaderShell;
